// packages/api-client/src/services/promptService.ts
import { getApiClient } from '../client';
import { handleApiError } from '../utils/handleApiError';
import type {
  PromptResponse,
  PromptCreatePayload,
  PromptUpdatePayload,
  ListPromptsFilters,
  ApiSuccessResponse,
  ServiceResponse,
  UUID,
  PaginatedResponse,
} from '@dopomogai/types';

export const promptService = {
  getAllPrompts: (filters: ListPromptsFilters = {}): ServiceResponse<PromptResponse[]> => {
    const queryParams = new URLSearchParams();
    if (filters.agent_id) {
      queryParams.append('agent_id', filters.agent_id);
    }
    if (filters.hasOwnProperty('unlinked_only') && filters.unlinked_only !== undefined) {
      queryParams.append('unlinked_only', String(filters.unlinked_only));
    }

    const config = queryParams.toString() ? { params: queryParams } : {};
    return getApiClient().get<PromptResponse[]>('/prompts', config)
      .then(response => response.data)
      .catch(err => handleApiError(err, 'listing prompts'));
  },

  getPromptById: (promptId: UUID): ServiceResponse<PromptResponse> => {
    return getApiClient().get<PromptResponse>(`/prompts/${promptId}`)
      .then(response => response.data)
      .catch(err => handleApiError(err, `fetching prompt ${promptId}`));
  },

  createPrompt: (payload: PromptCreatePayload): ServiceResponse<PromptResponse> => {
    return getApiClient().post<PromptResponse>('/prompts', payload)
      .then(response => response.data)
      .catch(err => handleApiError(err, 'creating prompt'));
  },

  updatePrompt: (promptId: UUID, payload: PromptUpdatePayload): ServiceResponse<PromptResponse> => {
    return getApiClient().put<PromptResponse>(`/prompts/${promptId}`, payload)
      .then(response => response.data)
      .catch(err => handleApiError(err, `updating prompt ${promptId}`));
  },

  deletePrompt: (promptId: UUID): ServiceResponse<ApiSuccessResponse> => {
    return getApiClient().delete(`/prompts/${promptId}`)
      // FIX: Changed to match the ApiSuccessResponse interface
      .then(() => ({ status: 'success' } as const))
      .catch(err => handleApiError(err, `deleting prompt ${promptId}`));
  },

  listAgentPromptHistory: (agentId: UUID): ServiceResponse<import('@dopomogai/types').PromptHistoryItem[]> => {
    return getApiClient()
      .get<import('@dopomogai/types').PromptHistoryItem[]>(`/prompts/agents/${agentId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `listing prompt history for agent ${agentId}`));
  },


  savePromptVersionAndActivate: (
    agentId: UUID,
    payload: import('@dopomogai/types').PromptSaveRequest
  ): ServiceResponse<import('@dopomogai/types').PromptHistoryItem[]> => {
    return getApiClient()
      .post<import('@dopomogai/types').PromptHistoryItem[]>(`/prompts/agents/${agentId}`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `saving new prompt for agent ${agentId}`));
  },
};